import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import {
  AppBar,
  Avatar,
  Container,
  IconButton,
  Link,
  Toolbar,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoImage from "../img/logo.png";
import BannerImageDark from "../img/new_banner-dark.png";
import BannerImage from "../img/new_banner.png";

const navbarLinks: { [key: string]: string } = {
  "/": "Lyrics",
  "/album/": "Album",
  "/curiosities": "Curiosities"
};

interface IProps {
  user: SpotifyApi.UserObjectPrivate | null;
  onThemeToggle: () => void;
  isDarkMode: boolean;
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  gap: "clamp(8px, 1.5vw, 16px)",
  minWidth: 0,
  padding: 0,
  [theme.breakpoints.down("sm")]: {
    minHeight: 52
  }
}));

const StyledLogo = styled("img")({
  cursor: "pointer",
  display: "block",
  flexShrink: 0
});

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 1,
  minWidth: 0,
  textDecoration: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    color: theme.palette.text.primary,
    textDecoration: "none"
  }
}));

const StyledActiveLink = styled(StyledLink)(({ theme }) => ({
  color: theme.palette.text.primary
}));

const StyledUserIcon = styled(Avatar)({
  width: 30,
  height: 30
});

const Grow = styled("div")({
  flexGrow: 1,
  minWidth: 0
});

const NavigationLinks = styled("nav")({
  alignItems: "center",
  display: "flex",
  flexShrink: 1,
  gap: "clamp(8px, 1.5vw, 16px)",
  minWidth: 0
});

const Navigation: React.FunctionComponent<IProps> = ({ user, onThemeToggle, isDarkMode }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();

  const currentPath = location.pathname;

  const showFullLogo = useMediaQuery(theme.breakpoints.up("md"));

  const goTo = (location: string) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (user !== null) {
      navigate(location);
    } else {
      navigate("/");
    }
  };

  const onUserIconClick = () => {
    if (user !== null) {
      navigate("/settings");
    } else {
      navigate("/");
    }
  };

  function showHelpPage(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.preventDefault();
    navigate("/help");
  }

  return (
    <StyledAppBar position="static">
      <Container maxWidth="md">
        <StyledToolbar>
          <Link href="/" onClick={goTo("/")}>
            {showFullLogo ? (
              <StyledLogo
                src={isDarkMode ? BannerImageDark : BannerImage}
                height="30"
                alt="Spotify Panel Banner"
              />
            ) : (
              <StyledLogo src={LogoImage} height="30" alt="Spotify Panel Logo" />
            )}
          </Link>

          <NavigationLinks aria-label="Primary navigation">
            {Object.keys(navbarLinks).map(path =>
              currentPath === path ? (
                <StyledActiveLink key={path} href={path} onClick={goTo(path)}>
                  <Typography variant="body1">{navbarLinks[path]}</Typography>
                </StyledActiveLink>
              ) : (
                <StyledLink key={path} href={path} onClick={goTo(path)}>
                  <Typography variant="body1">{navbarLinks[path]}</Typography>
                </StyledLink>
              )
            )}
          </NavigationLinks>

          <Grow />

          <IconButton onClick={showHelpPage} sx={{ flexShrink: 0 }}>
            <HelpIcon />
          </IconButton>

          <IconButton onClick={onUserIconClick} sx={{ flexShrink: 0 }}>
            {user !== null ? (
              <StyledUserIcon
                alt={user.display_name + " Logo"}
                src={
                  user.images !== undefined && user.images.length > 0
                    ? user.images[0].url
                    : undefined
                }
              >
                {user.display_name?.substring(0, 1)}
              </StyledUserIcon>
            ) : (
              <AccountCircleIcon />
            )}
          </IconButton>
        </StyledToolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navigation;
